import type { Cluster } from "@oxminter/shared/cluster";

/**
 * Building the off-chain metadata document, and deciding when a URI may be
 * filled in for the operator.
 *
 * Type-only import, so this module is erased to zero dependencies at runtime
 * and can be unit-tested without loading web3.js — see invariant 23.
 */

export interface MetadataInput {
  name: string;
  symbol: string;
  description?: string;
  image?: string;
}

export interface MetadataDoc {
  name: string;
  symbol: string;
  description: string;
  image: string;
  external_url?: string;
}

/** Metaplex stores the URI in a fixed 200-byte field. */
export const URI_MAX = 200;

/** The document shipped with the console, deployed at /meta/ on every build. */
export const REHEARSAL_SLUG = "devnet-rehearsal";

/**
 * A filename from a token name. Lower case, hyphen separated, no path
 * characters — this becomes a URL segment, so anything that could change the
 * path's meaning is dropped rather than escaped.
 */
export function metadataSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/**
 * Builds the document from what the operator has already typed into the form.
 * `image` is required by every wallet and by probeMetadataUri, so it falls back
 * to the console's own logo rather than being emitted empty — a document with
 * an empty image string passes a naive check and still renders as a blank
 * square.
 */
export function buildMetadataDoc(input: MetadataInput, origin: string): MetadataDoc {
  const image = input.image?.trim() ? input.image.trim() : `${origin}/logo-256.png`;
  const doc: MetadataDoc = {
    name: input.name.trim(),
    symbol: input.symbol.trim(),
    description: (input.description ?? "").trim(),
    image,
  };
  return doc;
}

/** Where a document with this slug will answer from, once deployed. */
export function metadataUriFor(origin: string, slug: string): string {
  return `${origin.replace(/\/+$/, "")}/meta/${slug}.json`;
}

/** Same rules the mint checklist applies, so the two cannot disagree. */
export function uriAcceptable(uri: string): boolean {
  return /^(https:\/\/|ipfs:\/\/|ar:\/\/)/.test(uri) && uri.length <= URI_MAX;
}

/**
 * The URI to fill in for the operator, or null when nothing may be filled in.
 *
 * MAINNET ALWAYS RETURNS NULL, and that is the whole point of this function
 * existing rather than the caller assembling a string. The console ships one
 * ready-made document, and it describes a rehearsal token. Auto-filling it on
 * mainnet would mint a real, irreversible token named "oxMinter Devnet
 * Rehearsal" pointing at a placeholder image — with the metadata written before
 * authorities are revoked, so unfixable. A convenience that saves ten seconds
 * on devnet must not be able to do that on mainnet.
 */
export function autoFillUri(cluster: Cluster, origin: string): string | null {
  if (cluster !== "devnet") return null;
  const uri = metadataUriFor(origin, REHEARSAL_SLUG);
  // Never fill in something the mint checklist will then reject. A local dev
  // origin is http, which fails the https/ipfs/ar rule -- filling it in would
  // leave the operator staring at a populated field and a red requirement,
  // which is worse than an empty one. Caught by the browser test, not by the
  // unit tests, which had checked the two halves separately and never together.
  return uriAcceptable(uri) ? uri : null;
}

